import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import StatusBadge from '../components/common/InvoiceStatusBadge';
import invoiceCreatorStyles from '../styles/invoiceCreator.module.css';

const formatDate = (timestamp) => {
  if (!timestamp || !timestamp.toDate) {
    return '---';
  }
  return timestamp.toDate().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatCurrency = (amount) => {
  const numberAmount = Number(amount) || 0; 
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(numberAmount);
};

const InvoiceDetailPage = () => {
  const { invoiceId } = useParams();
  const { jeweller } = useAuth();

  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!jeweller?.id || !invoiceId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const invoiceRef = doc(db, 'invoices', invoiceId);
        const docSnap = await getDoc(invoiceRef);

        if (docSnap.exists()) {
          const invoiceData = { id: docSnap.id, ...docSnap.data() };

          if (invoiceData.jewellerId !== jeweller.id) {
            setError("You do not have permission to view this invoice.");
          } else {
            setInvoice(invoiceData);
          }
        } else {
          setError("Invoice not found.");
        }
      } catch (err) {
        console.error("Error fetching invoice details:", err);
        setError("An error occurred while fetching the invoice.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceId, jeweller]);

  if (isLoading) {
    return <div className="p-8 text-center">Loading invoice...</div>;
  }

  if (error) {
    return <div className="p-8 text-center text-red-500">{error}</div>;
  }
  
  if (!invoice) {
    return <div className="p-8 text-center">Invoice not found.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-8">
      
      {/* PAGE HEADER */}
      <div className="bg-white rounded-lg shadow p-6">
        <Link to="/invoices" className={invoiceCreatorStyles.backButton}>
          &larr; Back to invoices
        </Link>
        <div className="flex justify-between items-start mt-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Invoice Details
            </h1>
            <p className="text-gray-500 mt-1">
              {invoice.stripe?.invoiceNumber || invoice.uniqueRefNumber || `Invoice ${invoice.id.slice(0, 8)}`}
            </p>
          </div>
          <StatusBadge status={invoice.status} dueDate={invoice.dueDate} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* COLUMN 1: ACTIVITY */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* ACTIVITY SECTION */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Activity Timeline</h2>
            <div className="space-y-4">
              
              {/* Payout Event */}
              {invoice.stripe?.payoutStatus === 'paid_out' && invoice.stripe?.payoutDate && (
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      Funds deposited to your bank account
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDate(invoice.stripe.payoutDate)} • Amount: {formatCurrency(invoice.financials?.netPayout)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Payout ID: {invoice.stripe.payoutId}
                    </p>
                  </div>
                </div>
              )}

              {/* Payment Event */}
              {invoice.status === 'paid' && (
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'var(--Green-100)' }}
                    >
                      <svg className="w-5 h-5" style={{ color: 'var(--Green-700)' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      Invoice was paid by customer
                    </p>
                    <p className="text-sm text-gray-500">
                      {invoice.stripe?.paidAt ? formatDate(invoice.stripe.paidAt) : formatDate(invoice.updatedAt)}
                    </p>
                  </div>
                </div>
              )}

              {/* Sent Event */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    Invoice was sent to customer
                  </p>
                  <p className="text-sm text-gray-500">
                    {invoice.sentAt ? formatDate(invoice.sentAt) : formatDate(invoice.createdAt)}
                  </p>
                </div>
              </div>

            </div>
          </div>

          {/* INVOICE DETAILS SECTION */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Invoice Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              
              {/* Customer Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                  Customer Information
                </h3>
                <div className="space-y-2">
                  <p className="font-medium text-gray-900">{invoice.customerInfo.name}</p>
                  <p className="text-gray-600">{invoice.customerInfo.email}</p>
                </div>
              </div>

              {/* Invoice Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                  Invoice Information
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Due Date:</span>
                    <span className="font-medium">{formatDate(invoice.dueDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <StatusBadge status={invoice.status} dueDate={invoice.dueDate} size="sm" />
                  </div>
                  {invoice.stripe?.hostedInvoiceUrl && (
                    <div className="mt-4">
                      <a 
                        href={invoice.stripe.hostedInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View Customer Invoice
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Line Items - Compact Format */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                Items ({invoice.lineItems.length})
              </h3>
              <div className="space-y-3">
                {invoice.lineItems.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.description}</p>
                      <p className="text-sm text-gray-500">
                        Quantity: {item.quantity} × {formatCurrency(item.price)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        {formatCurrency(item.quantity * item.price)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* COLUMN 2: PAYMENT SUMMARY */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6 sticky top-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Payment Summary</h2>
            
            {invoice.financials ? (
              <div className="space-y-4">
                
                {/* Subtotal */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium text-gray-900">{formatCurrency(invoice.financials.subtotal)}</span>
                </div>

                <hr className="border-gray-200" />

                {/* Platform Fees */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">
                      Boutee Commission
                      <span className="text-xs text-gray-400 block">
                        (Tiered rate applied)
                      </span>
                    </span>
                    <span className="text-gray-900">-{formatCurrency(invoice.financials.bouteeCommission)}</span>
                  </div>
                  
                  {invoice.financials.processingFee && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        Payment Processing Fee
                        <span className="text-xs text-gray-400 block">
                          (1.8% of subtotal)
                        </span>
                      </span>
                      <span className="text-gray-900">-{formatCurrency(invoice.financials.processingFee)}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center text-sm font-medium">
                    <span className="text-gray-600">Total Platform Fees</span>
                    <span className="text-gray-900">-{formatCurrency(invoice.financials.totalPlatformFee)}</span>
                  </div>
                </div>

                <hr className="border-gray-200" />

                {/* Net Payout */}
                <div className="flex justify-between items-center text-lg font-bold">
                  <span className="text-gray-900">Your Payout</span>
                  <span className="text-gray-900">{formatCurrency(invoice.financials.netPayout)}</span>
                </div>

                {/* Payout Status */}
                {invoice.stripe?.payoutStatus === 'paid_out' ? (
                  <div
                    className="rounded-lg p-4 mt-4 border"
                    style={{
                      background: 'var(--brand-gradient-4)',
                      borderColor: 'var(--Green-300)',
                    }}
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" style={{ color: 'var(--Green-900)' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-medium text-gray-900">
                        Paid to your account
                      </span>
                    </div>
                    <p className="text-xs text-gray-900 mt-1">
                      Deposited on {formatDate(invoice.stripe.payoutDate)}
                    </p>
                  </div>
                ) : invoice.status === 'paid' ? (
                  <div
                    className="rounded-lg p-4 mt-4 border"
                    style={{
                      background: 'linear-gradient(135deg, var(--Blue-500), var(--Blue-600))',
                      borderColor: 'var(--Blue-300)',
                    }}
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" style={{ color: 'var(--Brand-White)' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-medium text-white">
                        Payout processing
                      </span>
                    </div>
                    <p className="text-xs text-white mt-1">
                      Funds will be transferred to your account within 2-7 business days
                    </p>
                  </div>
                ) : (
                  <div
                    className="rounded-lg p-4 mt-4 border"
                    style={{
                      background: 'var(--Amber-100)',
                      borderColor: 'var(--Amber-200)',
                    }}
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" style={{ color: 'var(--Amber-700)' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H7z" clipRule="evenodd" />
                        <path d="M9 9h2v2H9V9z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-900">
                        Awaiting payment
                      </span>
                    </div>
                    <p className="text-xs text-amber-800 mt-1">
                      Payout will be processed once customer pays
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  Financial details will be available once the invoice is processed.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailPage;
